/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiComboBox, EuiFlexGroup, EuiFlexItem, EuiFormRow } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  isCloudSpecificProjectRoleId,
  isProjectResourceType,
  type ResourceType,
} from '@modules/role-assignments-lib/types'

import ApiAccessSelector from '../ApiAccessSelector'

import type { ApiAccessOptions } from '../ApiAccessSelector/types'

export type AllRoleSelectorAccessProps<RoleId extends string> = {
  options: Array<EuiComboBoxOptionOption<RoleId>>
  singleSelection: boolean
  singleSelectionAsCount: number
  noRolesPlaceholder: (singleSelection: boolean) => string
  apiAccessValue: ApiAccessOptions
  setApiAccessValue: (value: ApiAccessOptions) => void
  'data-test-id': string
  onChange: (selectedOptions: Array<EuiComboBoxOptionOption<RoleId>>) => void
  selectedOptions: Array<EuiComboBoxOptionOption<RoleId>>
  isApiKey?: boolean
  resourceType: ResourceType
}

const AllRoleSelectorAccess = <RoleId extends string>({
  options,
  singleSelection,
  singleSelectionAsCount,
  noRolesPlaceholder,
  apiAccessValue,
  setApiAccessValue,
  'data-test-id': dataTestId,
  onChange,
  selectedOptions,
  isApiKey,
  resourceType,
}: AllRoleSelectorAccessProps<RoleId>): JSX.Element => (
  <EuiFlexGroup direction='row' gutterSize='s' alignItems='flexStart' style={{ maxWidth: '60rem' }}>
    {' '}
    <EuiFlexItem>
      <EuiFormRow
        label={
          <FormattedMessage
            id='roles-assignments.role.selector.title'
            defaultMessage='{count, plural, one {Role} other {Roles}}'
            values={{
              count: singleSelectionAsCount,
            }}
          />
        }
        fullWidth={true}
        helpText={
          <FormattedMessage
            id='roles-assignments.role.selector.help'
            defaultMessage='{count, plural, one {This role} other {These roles}} will apply to any existing and future Elastic Cloud resources.'
            values={{
              count: singleSelectionAsCount,
            }}
          />
        }
      >
        <EuiComboBox<RoleId>
          data-test-subj='allRoleSelector'
          data-test-id={dataTestId}
          placeholder={noRolesPlaceholder(singleSelection)}
          singleSelection={singleSelection}
          options={options}
          selectedOptions={selectedOptions}
          onChange={onChange}
        />
      </EuiFormRow>
    </EuiFlexItem>
    {isProjectResourceType(resourceType) && (
      <ApiAccessSelector
        onChange={(value) => setApiAccessValue(value)}
        containsNonCloudSpecificProjectRoles={selectedOptions.some(
          (option) => !isCloudSpecificProjectRoleId(option.value ?? ''),
        )}
        defaultValue={apiAccessValue}
        isApiKey={isApiKey}
        resourceType={resourceType}
      />
    )}
  </EuiFlexGroup>
)

export default AllRoleSelectorAccess
